// R-NAV-6 — real 404 page (spec §4.10). Route: catch-all `*`.
//
// Before: an unknown path rendered the landing page. Now it renders this.
//
// Mounted OUTSIDE the AppShell layout (a top-level sibling of the layout
// route in router.tsx) on purpose: AppShell classifies any unknown first
// segment as WORKSPACE and would bounce an anonymous visitor to /login — so a
// bad URL would never show a 404 to a logged-out user. Rendering standalone
// (still inside Theme/Auth/Role providers + the Router) lets BOTH anon and
// authed visitors see a proper 404 with a way home. Self-contained chrome
// (no Nav/Footer) keeps it independent of the auth gate.
import React from "react";

import { Button } from "../ui";
import { useAuth } from "../auth/AuthContext";
import { useRole } from "../role";
import { useGo } from "../router";

const NotFoundPage = (): React.ReactElement => {
  const go = useGo();
  const { isAuthenticated } = useAuth();
  const { role, activeRoleId } = useRole();
  // Authed → back to the role's home; anon → the public landing.
  const homeRoute = isAuthenticated && activeRoleId ? role.homeRoute : "home";

  return (
    <main
      id="not-found"
      data-screen-label="404 · یافت‌نشد"
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--bg)",
        color: "var(--fg)",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <div
          aria-hidden="true"
          style={{ fontSize: 72, fontWeight: 800, lineHeight: 1, color: "var(--accent)", fontFamily: "var(--f-mono)" }}
        >
          ۴۰۴
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginTop: 16 }}>صفحه پیدا نشد</h1>
        <p style={{ fontSize: 14, color: "var(--fg-mute)", lineHeight: 1.7, marginTop: 10 }}>
          نشانی‌ای که دنبالش بودید وجود ندارد یا جابه‌جا شده است. می‌توانید به خانه
          بازگردید یا از جست‌وجو استفاده کنید.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 24, flexWrap: "wrap" }}>
          <Button variant="primary" size="md" onClick={() => go(homeRoute)}>
            بازگشت به خانه
          </Button>
          {isAuthenticated ? (
            <Button variant="outline" size="md" onClick={() => go("search")}>
              جست‌وجو
            </Button>
          ) : (
            <Button variant="outline" size="md" onClick={() => go("programs")}>
              مشاهدهٔ برنامه‌ها
            </Button>
          )}
        </div>
      </div>
    </main>
  );
};

export default NotFoundPage;
